import React from 'react';
import { getBadge } from '../../utilities/badgeDefinitions';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';

const UserBadge = ({ badgeId, className = '', size = 'sm' }) => {
  const badge = getBadge(badgeId);

  if (!badge) {
    return null;
  }

  const Icon = badge.icon;

  // Size configurations
  const sizeClasses = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
    xl: 'w-8 h-8'
  };

  const containerSizes = {
    xs: 'w-4 h-4',
    sm: 'w-5 h-5',
    md: 'w-6 h-6',
    lg: 'w-7 h-7',
    xl: 'w-9 h-9'
  };

  // Create dynamic styles for glow effect
  const glowStyles = badge.hasGlow
    ? {
        boxShadow: `
      0 0 10px ${badge.glowColor}40,
      0 0 20px ${badge.glowColor}30,
      0 0 30px ${badge.glowColor}20,
      inset 0 0 10px ${badge.glowColor}10
    `,
        animation: 'badgeGlow 2s ease-in-out infinite alternate'
      }
    : {};

  return (
    <>
      {/* Add keyframes for glow animation */}
      <style jsx>{`
        @keyframes badgeGlow {
          from {
            box-shadow: 0 0 5px ${badge.glowColor}40,
              0 0 10px ${badge.glowColor}30, 0 0 15px ${badge.glowColor}20,
              inset 0 0 5px ${badge.glowColor}10;
          }
          to {
            box-shadow: 0 0 15px ${badge.glowColor}60,
              0 0 25px ${badge.glowColor}40, 0 0 35px ${badge.glowColor}30,
              inset 0 0 15px ${badge.glowColor}20;
          }
        }

        .badge-container {
          transition: all 0.3s ease;
        }

        .badge-container:hover {
          transform: scale(1.2);
        }
      `}</style>

      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={`
                badge-container
                ${containerSizes[size]} 
                rounded-full 
                flex items-center justify-center
                border-2
                shadow-sm
                cursor-help
                ${className}
              `}
              style={{
                backgroundColor: badge.bgColor,
                borderColor: badge.borderColor,
                ...glowStyles
              }}
            >
              <Icon
                className={sizeClasses[size]}
                style={{
                  color: badge.color,
                  filter: badge.hasGlow
                    ? 'drop-shadow(0 0 4px currentColor)'
                    : 'none'
                }}
              />
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <div className='text-center'>
              <div className='text-xs'>{badge.name}</div>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </>
  );
};

export { UserBadge };
